import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { Broadcast } from '@/features/broadcasts/broadcasts-types';
import {
  useCreateBroadcast,
  useUpdateBroadcast,
} from '@/features/broadcasts/useBroadcasts';
import type { ProactivenessRollout } from '@/features/im-channel-proactivity-experiments/im-channel-proactivity-experiments-types';

import type { FormEvent } from 'react';

type Props = {
  broadcast?: Broadcast;
  rollouts: ProactivenessRollout[];
  onOpenChange: (open: boolean) => void;
};

export const BroadcastEditorDialog = ({
  broadcast,
  rollouts,
  onOpenChange,
}: Props) => {
  const createBroadcast = useCreateBroadcast();
  const updateBroadcast = useUpdateBroadcast();
  const mutation = broadcast ? updateBroadcast : createBroadcast;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const input = {
      title: String(form.get('title')),
      text: String(form.get('text')),
    };
    const onSuccess = () => onOpenChange(false);
    if (broadcast) {
      updateBroadcast.mutate({ id: broadcast.id, input }, { onSuccess });
    } else {
      createBroadcast.mutate(
        { ...input, rollout_slug: String(form.get('rollout_slug')) },
        { onSuccess }
      );
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          <DialogHeader>
            <DialogTitle>
              {broadcast ? 'Edit broadcast' : 'Create broadcast'}
            </DialogTitle>
            <DialogDescription>
              The message is delivered exactly as written, without model
              generation.
            </DialogDescription>
          </DialogHeader>
          <label className="block space-y-1.5" htmlFor="broadcast-rollout">
            <span className="text-sm font-medium">Rollout</span>
            <Select
              id="broadcast-rollout"
              name="rollout_slug"
              defaultValue={broadcast?.rollout_slug ?? rollouts[0]?.slug}
              disabled={Boolean(broadcast)}
              required
            >
              {rollouts.map((rollout) => (
                <option key={rollout.slug} value={rollout.slug}>
                  {rollout.slug}
                </option>
              ))}
            </Select>
          </label>
          <label className="block space-y-1.5" htmlFor="broadcast-title">
            <span className="text-sm font-medium">Name</span>
            <Input
              id="broadcast-title"
              name="title"
              defaultValue={broadcast?.title}
              autoFocus
              required
            />
          </label>
          <label className="block space-y-1.5" htmlFor="broadcast-text">
            <span className="text-sm font-medium">Message</span>
            <Textarea
              id="broadcast-text"
              name="text"
              defaultValue={broadcast?.text}
              className="min-h-40"
              required
            />
          </label>
          {mutation.error && (
            <p role="alert" className="text-sm text-destructive">
              {mutation.error.message}
            </p>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : broadcast ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
